import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { Manifest, Skill } from '../types';
import { fetchSkillRegistry } from '../lib/marketplace';

const REFRESH_COOLDOWN_MS = 3_000;

interface ManifestContextValue {
  skills: Skill[];
  loading: boolean;
  error: string | null;
  lastUpdated: string | null;
  refresh: () => void;
  canRefresh: boolean;
  cooldownRemaining: number;
}

const ManifestContext = createContext<ManifestContextValue | null>(null);

export function ManifestProvider({ children }: { children: React.ReactNode }) {
  const [manifest, setManifest] = useState<Manifest | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefreshTime, setLastRefreshTime] = useState(0);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadManifest = useCallback(async (isBackground = false) => {
    if (!isBackground) setLoading(true);
    try {
      const fresh = await fetchSkillRegistry();
      setManifest((prev) => {
        if (!prev || prev.generated_at !== fresh.generated_at) {
          return fresh;
        }
        return prev;
      });
      setError(null);
    } catch (err: any) {
      if (!isBackground) setError(err.message);
    } finally {
      if (!isBackground) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadManifest();
  }, [loadManifest]);

  const startCooldown = useCallback(() => {
    setLastRefreshTime(Date.now());
    setCooldownRemaining(REFRESH_COOLDOWN_MS);
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setCooldownRemaining((prev) => {
        const remaining = Math.max(0, prev - 1000);
        if (remaining === 0 && cooldownRef.current) {
          clearInterval(cooldownRef.current);
          cooldownRef.current = null;
        }
        return remaining;
      });
    }, 1000);
  }, []);

  useEffect(() => {
    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    };
  }, []);

  const refresh = useCallback(() => {
    const now = Date.now();
    if (now - lastRefreshTime < REFRESH_COOLDOWN_MS) return;
    startCooldown();
    loadManifest(true);
  }, [lastRefreshTime, startCooldown, loadManifest]);

  const canRefresh = cooldownRemaining === 0;

  return (
    <ManifestContext.Provider
      value={{
        skills: manifest?.skills || [],
        loading,
        error,
        lastUpdated: manifest?.generated_at || null,
        refresh,
        canRefresh,
        cooldownRemaining,
      }}
    >
      {children}
    </ManifestContext.Provider>
  );
}

export function useManifest() {
  const ctx = useContext(ManifestContext);
  if (!ctx) throw new Error('useManifest must be used within ManifestProvider');
  return ctx;
}
